import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { onQueryParamChange } from '../select/object-select-input/object-select-input.controller';
import { DurationInputProps } from './duration-input.component';

interface ViewProps {
    showModal: boolean;
    changeShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    displayText: string | undefined;
    updateDisplayText: () => void;
    setDuration: React.Dispatch<React.SetStateAction<number | undefined>>;
    setTimeUnit: React.Dispatch<React.SetStateAction<string | undefined>>;
    setTimePeriod: React.Dispatch<React.SetStateAction<string | undefined>>;
    setLocalDuration: React.Dispatch<React.SetStateAction<number | undefined>>;
    setLocalUnit: React.Dispatch<React.SetStateAction<string | undefined>>;
    setLocalPeriod: React.Dispatch<React.SetStateAction<string | undefined>>;
    formatToDate: () => { key: string; value: string | undefined };
    isFilledIn: () => boolean;
    dropdownRef: React.RefObject<HTMLDivElement | null>;
    duration?: number;
    timeUnit?: string;
    timePeriod?: string;
    localDuration?: number;
    localTimeUnit?: string;
    localTimePeriod?: string;
    setValues: () => void;
    clear: () => void;
}

const useDurationInput = (props: DurationInputProps): ViewProps => {
    const [showModal, changeShowModal] = useState<boolean>(false);
    const [displayText, setDisplayText] = useState<string | undefined>();
    const [duration, setDuration] = useState<number | undefined>(props.duration);
    const [timeUnit, setTimeUnit] = useState<string | undefined>(props.timeUnit);
    const [timePeriod, setTimePeriod] = useState<string | undefined>(props.timePeriod);
    const [localDuration, setLocalDuration] = useState<number | undefined>(props.duration);
    const [localTimeUnit, setLocalUnit] = useState<string | undefined>(props.timeUnit);
    const [localTimePeriod, setLocalPeriod] = useState<string | undefined>(props.timePeriod);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const [, setSearchParams] = useSearchParams();

    useEffect(() => {
        if (props.duration) {
            setDuration(props.duration);
            setLocalDuration(props.duration);
        }
        if (props.timeUnit) {
            setTimeUnit(props.timeUnit);
            setLocalUnit(props.timeUnit);
        }
        if (props.timePeriod) {
            setTimePeriod(props.timePeriod);
            setLocalPeriod(props.timePeriod);
        }
    }, [props.duration, props.timeUnit, props.timePeriod]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                changeShowModal(false);
            }
        };

        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [dropdownRef]);

    useEffect(() => {
        updateDisplayText();
        if (duration && timeUnit && timePeriod) {
            const obj = formatToDate();
            props.onApplyFilter(duration, timeUnit, timePeriod, obj);
            props.allowURLChange && onQueryParamChange(obj.value, obj.key, setSearchParams);
        }
    }, [duration, timeUnit, timePeriod]);

    const convertTimeUnit = (unit: string, value: number): number => {
        let miliseconds = 0;
        switch (unit) {
            case 'Hours':
                miliseconds = value * 60 * 60 * 1000;
                break;
            case 'Days':
                miliseconds = value * 24 * 60 * 60 * 1000;
                break;
            case 'Weeks':
                miliseconds = value * 7 * 24 * 60 * 60 * 1000;
                break;
            case 'Months':
                miliseconds = value * 30 * 24 * 60 * 60 * 1000;
                break;
            case 'Years':
                miliseconds = value * 365 * 24 * 60 * 60 * 1000;
                break;
            default:
                break;
        }

        return miliseconds;
    };

    const isFilledIn = (): boolean => {
        return localDuration !== undefined && localTimeUnit !== undefined && localTimePeriod !== undefined;
    };

    const formatToDate = (): { key: string; value: string } => {
        const currentDate = Date.now();
        const fromDate = currentDate - convertTimeUnit(timeUnit!, duration!);
        const formattedDate = format(new Date(fromDate), "yyyy-MM-dd'T'HH:mm:ss.SSSSSSXXX");

        if (timePeriod === 'At least') {
            return { key: 'before', value: formattedDate };
        } else {
            return { key: 'after', value: formattedDate };
        }
    };

    const clear = () => {
        setDuration(undefined);
        setTimeUnit(undefined);
        setTimePeriod(undefined);
        setLocalDuration(undefined);
        setLocalUnit(undefined);
        setLocalPeriod(undefined);
        updateDisplayText();
    };

    const updateDisplayText = () => {
        let defaultText = undefined;
        if (duration && timeUnit && timePeriod) {
            defaultText = `${duration} ${timeUnit}`;
        }
        setDisplayText(defaultText);
    };

    const setValues = () => {
        setDuration(localDuration);
        setTimeUnit(localTimeUnit);
        setTimePeriod(localTimePeriod);
    };

    return {
        showModal,
        updateDisplayText,
        changeShowModal,
        displayText,
        setDuration,
        setTimeUnit,
        setTimePeriod,
        setLocalDuration,
        setLocalUnit,
        setLocalPeriod,
        formatToDate,
        isFilledIn,
        dropdownRef,
        duration,
        timeUnit,
        timePeriod,
        localDuration,
        localTimeUnit,
        localTimePeriod,
        setValues,
        clear
    };
};

export default useDurationInput;
